import * as React from 'react';

import {
  Badge,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Paper,
  Select, SelectChangeEvent,
  Stack,
  Typography,
  TextField,
} from '@mui/material';

import { grey } from '@mui/material/colors';

import FilterIcon from '@mui/icons-material/FilterAlt';
import AddIcon from '@mui/icons-material/Add';

import { EventReport, EventReportFilter } from '../../core/api/clients';

import { CamelCaseToSplitPascal } from '../Utility/Cases';

interface IncidentFormEventFilterSubheaderProps {
  activeFilters: EventReportFilter[];
  filterKeys: (keyof EventReport)[];
  onFilterAdd: (filter: EventReportFilter) => void;
  onFilterRemove: (filter: EventReportFilter) => void;
}

function IncidentFormEventFilterSubheader({
  activeFilters,
  filterKeys,
  onFilterAdd,
  onFilterRemove,
}: IncidentFormEventFilterSubheaderProps): React.JSX.Element {
  const [isFilterSectionExpanded, setIsFilterSectionExpanded] = React.useState<boolean>(false);

  const [newFilterKey, setNewFilterKey] = React.useState<string | undefined>(undefined);
  const [newFilterValue, setNewFilterValue] = React.useState<string | undefined>(undefined);

  const handleNewFilterKeyChange = (e: SelectChangeEvent<string>) => setNewFilterKey(e.target.value);

  const handleNewFilterAdd = () => {
    if (newFilterKey !== '' && newFilterValue !== '') {
      onFilterAdd({
        field: newFilterKey,
        value: newFilterValue,
      });
    }
  };

  return (
    <ListSubheader>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ width: '100%' }}
      >
        <Typography variant="subtitle1">
          Available Reports
        </Typography>
        <IconButton size="small" onClick={() => setIsFilterSectionExpanded((p) => !p)}>
          <Badge badgeContent={activeFilters.length} color="primary">
            <FilterIcon />
          </Badge>
        </IconButton>
      </Box>
      <Collapse in={isFilterSectionExpanded}>
        <Paper>
          <Stack
            spacing={2}
            direction="row"
            alignItems="center"
            justifyContent="flex-start"
            sx={{ width: '100%', p: 2, backgroundColor: grey['100'] }}
          >
            {activeFilters.map((filter) => (
              <Chip
                key={filter.field}
                color="success"
                label={`${CamelCaseToSplitPascal(filter.field ?? '')}: ${filter.value}`}
                onDelete={() => onFilterRemove(filter)}
              />
            ))}
          </Stack>
          <Divider />
          <Stack
            spacing={2}
            direction="row"
            justifyContent="space-between"
            sx={{ p: 2, backgroundColor: grey['50'] }}
          >
            <Box flexGrow={1}>
              <FormControl fullWidth size="small" variant="standard">
                <InputLabel>Filter</InputLabel>
                <Select value={newFilterKey} onChange={handleNewFilterKeyChange}>
                  {filterKeys.map((filterKey) => (
                    <MenuItem key={filterKey} value={filterKey}>
                      {CamelCaseToSplitPascal(filterKey)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box flexGrow={1}>
              <TextField
                fullWidth
                size="small"
                label="Filter Value"
                variant="standard"
                value={newFilterValue}
                onChange={(e) => setNewFilterValue(e.target.value)}
              />
            </Box>
            <Button
              variant="text"
              size="small"
              endIcon={<AddIcon />}
              disabled={newFilterKey === undefined && newFilterValue === undefined}
              onClick={handleNewFilterAdd}
            >
              Add Filter
            </Button>
          </Stack>
        </Paper>
      </Collapse>
    </ListSubheader>
  );
}

export default IncidentFormEventFilterSubheader;
